"use client";

import React from "react";
import { 
  Scissors, 
  Users, 
  Heart, 
  Pill, 
  Utensils, 
  CheckCircle2, 
  AlertTriangle, 
  Sparkles,
  Info
} from "lucide-react";

export interface PreStudyRecommendationsCardProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  readOnly?: boolean;
  variant?: "interactive" | "reminder";
  showErrorShake?: boolean;
  className?: string;
}

export const OFFICIAL_RECOMMENDATIONS = [
  {
    icon: Scissors,
    title: "Rasurado por Nuestro Especialista",
    text: "Nuestro especialista rasurará una zona amplia del abdomen (o área a evaluar) apenas llegue a domicilio para garantizar una adecuada calidad de imagen.",
    badge: "Esencial para imagen",
    color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
  },
  {
    icon: Users,
    title: "Dos Personas de Apoyo",
    text: "Idealmente asistir con dos personas para facilitar la contención del paciente durante el procedimiento.",
    badge: "Seguridad y confort",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/30"
  },
  {
    icon: Heart,
    title: "Procedimiento Indoloro",
    text: "La ecografía es un procedimiento no invasivo y no doloroso.",
    badge: "100% No invasivo",
    color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
  },
  {
    icon: Pill,
    title: "Manejo de Ansiedad / Agresión",
    text: "Si el paciente es agresivo o muy nervioso, es ideal preguntarle a tu médico veterinario por un protocolo de tranquilización (ej. Gabapentina en gatos, Trazodona en perros u otro indicado).",
    badge: "Bajo prescripción",
    color: "text-purple-400 bg-purple-500/10 border-purple-500/30"
  },
  {
    icon: Utensils,
    title: "Ayuno de 8 Horas",
    text: "Ayuno de 8 horas, siempre que esté indicado por el médico veterinario según el caso.",
    badge: "Recomendación clínica",
    color: "text-amber-400 bg-amber-500/10 border-amber-500/30"
  }
];

export default function PreStudyRecommendationsCard({
  checked = false,
  onChange,
  readOnly = false,
  variant = "interactive",
  showErrorShake = false,
  className = ""
}: PreStudyRecommendationsCardProps) {
  const isReminder = variant === "reminder" || readOnly;

  return (
    <div 
      className={`
        relative overflow-hidden rounded-3xl border transition-all duration-300
        ${showErrorShake ? "animate-bounce border-amber-500 bg-amber-950/20 shadow-[0_0_35px_rgba(245,158,11,0.35)]" : "border-cyan-500/30 bg-slate-900/80 backdrop-blur-xl shadow-[0_0_30px_rgba(6,182,212,0.12)]"}
        ${className}
      `}
    >
      {/* Background glow effects */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Header */}
      <div className="p-6 md:p-8 border-b border-slate-800/80">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg md:text-xl font-extrabold text-white tracking-tight">
                {isReminder ? "Protocolo y Recomendaciones Pre-Ecografía" : "Recomendaciones Oficiales para el Examen"}
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                SYNAPTIX
              </span>
            </div>
            <p className="text-xs md:text-sm text-slate-400 mt-1">
              {isReminder 
                ? "Por favor asegúrate de cumplir con estas recomendaciones antes de la llegada del médico especialista."
                : "Para garantizar la máxima calidad diagnóstica y el bienestar de tu mascota, por favor lee estas 5 indicaciones."}
            </p>
          </div>
        </div>
      </div>

      {/* 5 Recommendations Grid */}
      <div className="p-6 md:p-8 space-y-4">
        <div className="grid grid-cols-1 gap-4">
          {OFFICIAL_RECOMMENDATIONS.map((rec, idx) => {
            const IconComponent = rec.icon;
            return (
              <div 
                key={idx}
                className="group relative flex items-start gap-4 p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition-colors"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${rec.color}`}>
                  <IconComponent className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <h4 className="text-sm font-bold text-white group-hover:text-cyan-300 transition-colors">
                      {idx + 1}. {rec.title}
                    </h4>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${rec.color}`}>
                      {rec.badge}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-slate-300 leading-relaxed">
                    {rec.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mandatory Acceptance Footer (Interactive mode only) */}
      {!isReminder && (
        <div className={`p-6 md:p-8 border-t transition-colors ${showErrorShake ? "bg-amber-950/30 border-amber-500/50" : "bg-slate-950/80 border-slate-800/80"}`}>
          {showErrorShake && (
            <div className="flex items-center gap-2 text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4 text-xs font-semibold animate-pulse">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                Acción requerida: Debes confirmar que has leído y entiendes las recomendaciones para proceder.
              </span>
            </div>
          )}

          <label className="flex items-start md:items-center gap-3.5 cursor-pointer select-none group">
            <div className="relative flex items-center justify-center mt-0.5 md:mt-0">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                className="peer sr-only"
              />
              <div className="w-6 h-6 rounded-lg border-2 border-slate-600 bg-slate-900 peer-checked:bg-cyan-500 peer-checked:border-cyan-400 peer-focus:ring-2 peer-focus:ring-cyan-500/50 transition-all flex items-center justify-center group-hover:border-cyan-500/70">
                {checked && <CheckCircle2 className="w-4 h-4 text-slate-950 font-bold" />}
              </div>
            </div>
            <div className="flex-1">
              <span className={`text-sm font-bold transition-colors ${checked ? "text-cyan-300" : "text-white group-hover:text-cyan-200"}`}>
                He leído y entiendo las recomendaciones para el examen de mi mascota *
              </span>
              <span className="block text-xs text-slate-400 mt-0.5">
                Confirmas que tu paciente está preparado o que consultarás al veterinario tratante en caso de duda.
              </span>
            </div>
          </label>
        </div>
      )}

      {/* Reminder Mode Footer */}
      {isReminder && (
        <div className="p-4 px-6 md:px-8 bg-cyan-950/30 border-t border-cyan-500/20 flex items-center justify-between text-xs text-cyan-300 font-medium">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-cyan-400 shrink-0" />
            <span>Recuerda que estas pautas son para optimizar la exactitud diagnóstica.</span>
          </div>
          <span className="hidden md:inline font-mono text-[10px] uppercase text-cyan-400/80">SYNAPTIX CLINICAL PROTOCOL</span>
        </div>
      )}
    </div>
  );
}
